/**
 * PostopQuickDialog
 * -----------------
 * "Dar postoperatorio" desde el Parte del día de quirófano: se abre con el
 * paciente ya fijado, carga la plantilla (por defecto o la que se elija) y
 * despliega sus secciones EDITABLES — el mismo editor de siempre — para
 * marcar qué campos quedan (quitar lo que no aplique, añadir indicaciones).
 * Al confirmar: crea el documento con esas custom_sections y abre su PDF
 * para imprimir.
 *
 * Se abre desde dos sitios: el parte del día (con `citaId`) y la ficha del
 * paciente (solo `patient.patient_id`). La plantilla que sale elegida la dice
 * el servidor en los dos casos (ver `pedirHojaSugerida`).
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Printer, Plus, X } from 'lucide-react';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

function normalizeSections(sections) {
  return (sections || []).map((s) => ({
    title: s.title || '',
    items: (s.items || []).filter((x) => x !== null && x !== undefined),
    body: s.body || '',
  }));
}

/**
 * Qué hoja le toca, preguntado al SERVIDOR (informe del 22/9, M1).
 *
 * Antes lo decidía el parte en el navegador (con un código de concepto fijo) y
 * la ficha no decía nada, así que desde consulta la hoja se elegía a mano.
 * Una misma intervención puede hacerse en consulta o en quirófano, según
 * cómo se cite (22/9/2026). La regla que sabe leer las dos cosas
 * vive en el servidor (`/plantilla-sugerida`): aquí solo se pregunta, con la
 * cita si viene del parte y con el paciente si viene de su ficha.
 *
 * Devuelve `{ code, porQue }` («no se sabe» = code null) o `{ error }`. Un
 * fallo NO es «no se sabe»: la pantalla lo dice en vez de dejar elegir a mano
 * como si el servidor hubiera contestado.
 *
 * Exportada (23/9/2026): la página de Postoperatorios, la tercera puerta para
 * dar uno, pregunta lo mismo y con las mismas reglas.
 */
export function pedirHojaSugerida(citaId, patientId) {
  const params = {};
  if (citaId) params.cita_id = citaId;
  if (patientId) params.patient_id = patientId;
  if (!params.cita_id && !params.patient_id) return Promise.resolve({ code: null, porQue: '' });
  return api.get('/api/postoperatorios/plantilla-sugerida', { params })
    .then((r) => {
      const d = r?.data;
      // Un 200 que no trae la respuesta esperada es un fallo, no un «no se sabe».
      if (!d || typeof d !== 'object' || Array.isArray(d) || !('code' in d)) {
        return { error: 'respuesta inesperada del servidor' };
      }
      return { code: d.code || null, porQue: d.por_que || '' };
    })
    .catch((e) => ({ error: e?.response?.data?.detail || 'sin conexión con el servidor' }));
}

/**
 * Lo que se lee debajo del desplegable de la plantilla, igual en las tres
 * puertas (parte, ficha y página de Postoperatorios). `prefijo` hace los
 * data-testid: `${prefijo}-sugerencia-cargando`, `-sugerencia-error`,
 * `-elige` y `-propuesta`.
 *
 * Cuando no se propone ninguna, dice por qué (23/9/2026): «la cita está
 * anulada» o «no tiene ninguna intervención hasta hoy» no son lo mismo que
 * «no se sabe de qué cirugía es», y quien elige a mano tiene que saberlo.
 */
export function AvisoHojaSugerida({ sugerencia, templateId, templates, prefijo }) {
  if (sugerencia?.cargando) {
    return (
      <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1" data-testid={`${prefijo}-sugerencia-cargando`}>
        <Loader2 className="w-3 h-3 animate-spin" /> Buscando la hoja que le toca…
      </p>
    );
  }
  if (sugerencia?.error) {
    // El servidor no ha contestado: se dice, para que nadie tome el hueco
    // por «no hay hoja propia» (22/9, M1).
    return (
      <p className="text-xs text-red-700 mt-1" data-testid={`${prefijo}-sugerencia-error`}>
        No se ha podido saber qué hoja le toca ({sugerencia.error}). Elígela tú.
      </p>
    );
  }
  if (!templateId) {
    return (
      <p className="text-xs text-amber-700 mt-1" data-testid={`${prefijo}-elige`}>
        Elige la pauta que le toca a esta paciente: cada cirugía tiene la suya.
        {sugerencia && !sugerencia.code && sugerencia.porQue
          ? ` No se propone ninguna: ${sugerencia.porQue}.` : ''}
      </p>
    );
  }
  if (sugerencia?.code && (templates || []).find((t) => t.id === templateId)?.code === sugerencia.code) {
    return (
      <p className="text-xs text-muted-foreground mt-1" data-testid={`${prefijo}-propuesta`}>
        Propuesta: {sugerencia.porQue}.
      </p>
    );
  }
  return null;
}

export default function PostopQuickDialog({ open, onOpenChange, patient, citaId, surgeryDate, surgeryName, onCreated }) {
  const [templates, setTemplates] = useState(null); // null = cargando
  const [errorCarga, setErrorCarga] = useState(null);
  const [surgeons, setSurgeons] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [surgeonId, setSurgeonId] = useState('');
  const [sections, setSections] = useState([]);
  const [extraNotes, setExtraNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [intento, setIntento] = useState(0);   // para reintentar la carga
  const [sugerencia, setSugerencia] = useState(null); // { code, porQue } | { error }
  const patientId = patient?.patient_id || '';

  // Catálogos una vez por apertura (plantillas de postop + cirujanos) y la
  // hoja que le toca a esta paciente.
  useEffect(() => {
    if (!open) return;
    // Se recarga SIEMPRE al abrir: si la vez anterior falló, no queremos
    // heredar un estado a medias y enseñar el cuadro vacío (2026-08-05).
    let vivo = true;
    setExtraNotes('');
    setErrorCarga(null);
    setTemplates(null);
    setSugerencia(null);
    setTemplateId('');
    Promise.all([
      api.get('/api/postoperatorios/templates').then((r) => r.data),
      api.get('/api/surgeons').then((r) => r.data).catch(() => []),
      pedirHojaSugerida(citaId, patientId),
    ]).then(([tpls, surs, sug]) => {
      if (!vivo) return;
      setTemplates(Array.isArray(tpls) ? tpls : []);
      setSurgeons(Array.isArray(surs) ? surs : []);
      setSugerencia(sug);
      // La que diga el servidor, y solo cuando ha contestado: mientras carga
      // no hay nada elegido (el cuadro enseña «Cargando…»). Si no se sabe de
      // qué procedimiento es, o el servidor ha fallado, no se propone ninguna:
      // se elige. Caer en la de por defecto le daba a un paciente la pauta
      // de otra intervención (22/9).
      const suya = sug?.code && (Array.isArray(tpls) ? tpls : []).find((t) => t.code === sug.code);
      setTemplateId(suya?.id || '');
      setSurgeonId(((surs || []).find((s) => s.is_default) || (surs || [])[0])?.id || '');
    }).catch((e) => {
      // Antes esto se tragaba con `.catch(() => [])` y el diálogo se quedaba
      // en blanco sin decir por qué. Ahora se ve el error y se puede reintentar.
      if (!vivo) return;
      setTemplates([]);
      setErrorCarga(e?.response?.data?.detail || 'No se pudieron cargar las plantillas');
    });
    return () => { vivo = false; };
  }, [open, intento, citaId, patientId]);

  // Al elegir plantilla, desplegar sus secciones para editar.
  useEffect(() => {
    if (!templates) return;
    const tpl = templates.find((t) => t.id === templateId);
    setSections(normalizeSections(tpl?.sections).map((s) => ({
      ...s,
      items: s.items.length ? s.items : [''],
    })));
  }, [templateId, templates]);

  const setSection = (idx, key, value) =>
    setSections((prev) => prev.map((s, i) => (i === idx ? { ...s, [key]: value } : s)));
  const setItem = (sIdx, iIdx, value) =>
    setSections((prev) => prev.map((s, i) => (i === sIdx
      ? { ...s, items: s.items.map((it, j) => (j === iIdx ? value : it)) }
      : s)));
  const removeItem = (sIdx, iIdx) =>
    setSections((prev) => prev.map((s, i) => (i === sIdx
      ? { ...s, items: s.items.filter((_, j) => j !== iIdx) }
      : s)));
  const addItem = (sIdx) =>
    setSections((prev) => prev.map((s, i) => (i === sIdx ? { ...s, items: [...s.items, ''] } : s)));

  const crearEImprimir = async () => {
    if (!templateId) return toast.error('Selecciona una plantilla');
    setSaving(true);
    try {
      const cleanSections = sections
        .map((s) => ({
          title: s.title.trim(),
          items: s.items.map((it) => (it || '').trim()).filter(Boolean),
          body: (s.body || '').trim(),
        }))
        .filter((s) => s.title || s.items.length || s.body);
      const res = await api.post('/api/postoperatorios/documents', {
        patient_id: patient.patient_id,
        template_id: templateId,
        surgeon_id: surgeonId || null,
        surgery_date: surgeryDate || null,
        surgery_name: surgeryName || null,
        custom_sections: cleanSections,
        extra_notes: extraNotes || '',
      });
      toast.success('Postoperatorio creado');
      onOpenChange(false);
      onCreated?.(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al crear el postoperatorio');
    } finally {
      setSaving(false);
    }
  };

  if (!patient) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="postop-quick-dialog">
        <DialogHeader>
          <DialogTitle>Dar postoperatorio — {patient.patient_name}</DialogTitle>
        </DialogHeader>

        {templates === null ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
            <Loader2 className="w-4 h-4 animate-spin" /> Cargando plantillas…
          </div>
        ) : templates.length === 0 ? (
          <div className="py-8 text-center space-y-3" data-testid="postop-quick-vacio">
            <p className="text-sm font-medium text-[#13231E]">
              {errorCarga || 'No hay ninguna plantilla de postoperatorio activa.'}
            </p>
            <p className="text-xs text-muted-foreground">
              {errorCarga
                ? 'Puede ser un problema de conexión.'
                : 'Créala en Postoperatorios → Plantillas.'}
            </p>
            <Button type="button" variant="outline" onClick={() => setIntento((n) => n + 1)}>
              Reintentar
            </Button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Plantilla base</Label>
                <Select value={templateId} onValueChange={setTemplateId}>
                  <SelectTrigger data-testid="postop-quick-template">
                    <SelectValue placeholder="Elige la pauta de esta cirugía" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}{t.is_default ? ' (por defecto)' : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <AvisoHojaSugerida
                  sugerencia={sugerencia}
                  templateId={templateId}
                  templates={templates}
                  prefijo="postop-quick"
                />
              </div>
              <div>
                <Label>Cirujano</Label>
                <Select value={surgeonId} onValueChange={setSurgeonId}>
                  <SelectTrigger data-testid="postop-quick-surgeon"><SelectValue placeholder="Cirujano" /></SelectTrigger>
                  <SelectContent>
                    {surgeons.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <p className="text-xs text-muted-foreground mt-1">
              Cirugía: <strong>{surgeryName || '—'}</strong> · Fecha: <strong>{surgeryDate || '—'}</strong>
              <span className="ml-2 italic">Quita lo que no aplique a este paciente o añade indicaciones específicas.</span>
            </p>

            <div className="space-y-3 mt-2">
              {sections.map((s, sIdx) => (
                <div key={`qsec-${sIdx}`} className="border border-border rounded-md p-3 bg-[#F0F6F2]/50">
                  <Input
                    value={s.title}
                    onChange={(e) => setSection(sIdx, 'title', e.target.value)}
                    className="font-semibold mb-2"
                    placeholder="Título de la sección"
                  />
                  {s.items.map((it, iIdx) => (
                    <div key={`qit-${sIdx}-${iIdx}`} className="flex items-start gap-1.5 mb-1.5">
                      <Textarea
                        value={it}
                        onChange={(e) => setItem(sIdx, iIdx, e.target.value)}
                        rows={Math.min(3, Math.max(1, Math.ceil((it || '').length / 90)))}
                        className="text-xs flex-1 min-h-0"
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="text-destructive shrink-0 h-7 w-7 p-0"
                        onClick={() => removeItem(sIdx, iIdx)}
                        title="Quitar esta indicación"
                      >
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                  <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => addItem(sIdx)}>
                    <Plus className="w-3 h-3 mr-1" /> Añadir indicación
                  </Button>
                </div>
              ))}
            </div>

            <div className="mt-2">
              <Label>Notas extra (opcional)</Label>
              <Textarea
                value={extraNotes}
                onChange={(e) => setExtraNotes(e.target.value)}
                rows={2}
                placeholder="Indicaciones específicas para este paciente…"
                className="mt-1"
              />
            </div>
          </>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancelar</Button>
          <Button
            type="button"
            onClick={crearEImprimir}
            disabled={saving || !templates}
            className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
            data-testid="postop-quick-crear"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Printer className="w-4 h-4 mr-2" />}
            Crear e imprimir
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
